import { FontAwesome } from '@expo/vector-icons';
import { Image } from 'expo-image';
import { useState } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';

import { useDeleteCreditCard } from '@/api/hooks/paymentAccountsHooks';
import { TokenizedCreditCardData } from '@/app/(app)/paymentMethods/creditCardList';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { formatExpiryDate } from '@/utils/dateFormats';
import { getCreditCardLogo } from '@/utils/getCreditCardLogo';
import { i18n } from '@/utils/i18n';

import DotsLoader from '../DotsLoader';
import ConfirmWarningModal from '../modals/ConfirmWarningModal';
import Text from '../Text';

interface TokenizedCardViewProps {
  tokenizedCard: TokenizedCreditCardData;
  showDelete?: boolean;
  showChange?: boolean;
  onPress?: () => void;
  onDelete?: () => void;
}

const TokenizedCardView = ({
  tokenizedCard,
  showDelete = true,
  showChange = false,
  onPress,
  onDelete
}: TokenizedCardViewProps) => {
  const { showSuccess, showError } = useEndFlowModal();

  const { mutateAsync: deleteCard, isPending } = useDeleteCreditCard();

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const handleDelete = () => {
    setShowConfirmationModal(false);
    deleteCard(tokenizedCard.card_key ?? '', {
      onSuccess: () => {
        onDelete && onDelete();
        showSuccess({ title: i18n.t('end_flow.success_title'), message: i18n.t('payment_methods.card_deleted') });
      },
      onError: (e) => {
        console.error(e);
        showError({ message: e.message ?? i18n.t('payment_methods.card_delete_error') });
      }
    });
  };

  const itemView = () => {
    if (isPending)
      return (
        <View style={styles.loader}>
          <DotsLoader />
        </View>
      );

    return (
      <View style={styles.container}>
        <Image source={getCreditCardLogo(tokenizedCard.card_type.trim())} style={styles.logo} contentFit="contain" />
        <View style={styles.dataContainer}>
          <Text fontWeight="700" style={styles.number}>
            {i18n.t('credit_cards.finished_in', {
              NUMBER: tokenizedCard.card_number_last_four_digits
            })}
          </Text>
          <Text style={styles.expiry}>
            {i18n.t('credit_cards.expiry', {
              EXPIRY: formatExpiryDate(tokenizedCard.expiration_date)
            })}
          </Text>
          <Text style={styles.nameOnCard}>{tokenizedCard.name_on_card}</Text>
        </View>
        {showChange && (
          <View style={styles.changeContainer}>
            <FontAwesome name="exchange" size={24} color="gray" />
            <Text fontWeight="600" style={styles.change}>
              {i18n.t('mix.change')}
            </Text>
          </View>
        )}
        {showDelete && (
          <TouchableOpacity onPress={() => setShowConfirmationModal(true)}>
            <Text fontWeight="700" style={styles.delete}>
              {i18n.t('mix.delete')}
            </Text>
          </TouchableOpacity>
        )}
        <ConfirmWarningModal
          message={i18n.t('payment_methods.card_delete_confirm')}
          showPicker={showConfirmationModal}
          loading={isPending}
          onClosePicker={() => setShowConfirmationModal(false)}
          onConfirmTapped={handleDelete}
        />
      </View>
    );
  };

  if (onPress) {
    return <TouchableOpacity onPress={onPress}>{itemView()}</TouchableOpacity>;
  } else return itemView();
};

export default TokenizedCardView;

const styles = StyleSheet.create({
  loader: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center'
  },

  container: {
    flexDirection: 'row',
    alignItems: 'center'
  },

  logo: {
    height: 65,
    width: 65
  },

  dataContainer: {
    flex: 1,
    alignItems: 'flex-start',
    justifyContent: 'center',
    paddingStart: 10,
    gap: 5
  },

  number: {
    color: 'black',
    fontSize: 16
  },

  nameOnCard: {
    fontSize: 14,
    fontStyle: 'italic'
  },

  expiry: {
    fontSize: 14
  },

  changeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2.5
  },

  change: {
    color: 'gray'
  },

  delete: {
    color: 'red'
  }
});
